<template>
  <div class="org-page">
    <div class="nav-wrap">
      <NavigationBar />
    </div>

    <!-- Success modal (shown after contact form submission) -->
    <div
      v-if="isSuccessModalOpen"
      class="contact-modal-backdrop"
      role="dialog"
      aria-modal="true"
      :aria-label="$t('partnership-contact-success-title')"
      @click.self="closeSuccessModal"
    >
      <div class="contact-modal">
        <button class="contact-modal__close" type="button" @click="closeSuccessModal" aria-label="Close">
          <ModalCloseBtn />
        </button>
        <div class="contact-modal__body contact-modal__body--success">
          <p class="contact-success__title">{{ $t('partnership-contact-success-title') }}</p>
          <p class="contact-success__text">{{ $t('partnership-contact-success-text') }}</p>
        </div>
      </div>
    </div>

    <!-- Contact modal (opens from "СВЪРЖЕТЕ СЕ С НАС" buttons) -->
    <div
      v-if="isContactModalOpen"
      class="contact-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Свържете се с нас"
      @click.self="closeContactModal"
    >
      <div class="contact-modal">
        <button class="contact-modal__close" type="button" @click="closeContactModal" aria-label="Close">
          <ModalCloseBtn />
        </button>

        <div class="contact-modal__body">
          <label class="contact-modal__label" for="contactFirstName">Име</label>
          <input
            id="contactFirstName"
            class="contact-modal__input"
            type="text"
            autocomplete="given-name"
            placeholder="Име"
            v-model.trim="contactFirstName"
          />

          <label class="contact-modal__label" for="contactLastName">Фамилия</label>
          <input
            id="contactLastName"
            class="contact-modal__input"
            type="text"
            autocomplete="family-name"
            placeholder="Фамилия"
            v-model.trim="contactLastName"
          />

          <label class="contact-modal__label" for="contactEmail">Имейл адрес</label>
          <input
            id="contactEmail"
            class="contact-modal__input"
            type="email"
            inputmode="email"
            autocomplete="email"
            placeholder="name@example.com"
            v-model.trim="contactEmail"
          />

          <label class="contact-modal__label" for="contactCompany">Име на компания</label>
          <input
            id="contactCompany"
            class="contact-modal__input"
            type="text"
            autocomplete="organization"
            placeholder="Име на компания"
            v-model.trim="contactCompany"
          />

          <label class="contact-modal__label" for="contactMessage">Съобщение</label>
          <textarea
            id="contactMessage"
            class="contact-modal__textarea"
            placeholder="Съобщение"
            rows="4"
            v-model.trim="contactMessage"
          />

          <button
            class="contact-modal__submit"
            :class="{ 'contact-modal__submit--disabled': !isContactFormValid }"
            type="button"
            :disabled="!isContactFormValid"
            @click="submitContactForm"
          >
            ИЗПРАТИ
          </button>
        </div>
      </div>
    </div>

    <section class="hero container">
      <div class="hero-card row g-0">
        <div class="col-lg-7 hero-left">
          <h1 class="text-style-home-heading">
            <span class="fixed-line">{{ $t('partnership-hero-line-1') }}</span>
            <span class="fixed-line">{{ $t('partnership-hero-line-2') }}</span>
            <span class="fixed-line">{{ $t('partnership-hero-line-3') }}</span>
          </h1>
          <h3 class="main-text text-style-home-subheading">
            {{ $t('partnership-hero-subheading') }}
          </h3>
          <p class="secondary-text text-style-home-subtitle">
            {{ $t('partnership-hero-subtitle') }}
          </p>
          <button class="cta-btn" type="button" @click="openContactModal">
            {{ $t('partnership-contact-btn') }}
          </button>
        </div>
        <div class="col-lg-5 hero-right">
          <img src="/static/ection.png" alt="Mom and child" class="img-fluid" />
        </div>
      </div>
    </section>

    <section class="examples">
      <div class="container">
        <h2 class="examples-title text-style-home-heading"> {{ $t('example-title') }}</h2>

       <div class="examples-card-wrap">

  <!-- ЗДРАВЕТО -->
        <div class="example-card">
          <div class="thumb">
            <img
              src="/static/partnership/zdraveto-video-thumb.png"
              alt=""
            />
          </div>

          <div class="example-body">
            {{ $t('zdraveto-example-text') }}
          </div>

          <router-link
            class="read-more"
            :to="{ name: 'zdraveto-initiative-2026' }"
          >
            {{ $t('zdraveto-learn-more-2') }}
          </router-link>
        </div>

        <!-- TRANSPRESS -->
        <div class="example-card">
          <div class="thumb transpress-thumb">
            <img
              src="/static/transpress/hero.png"
              alt="TRANSPRESS × Momfident"
            />
          </div>

          <div class="example-body">
            {{ $t('transpress-example-text') }}
          </div>

          <router-link
            class="read-more"
            :to="{ name: 'transpress-2026' }"
          >
            {{ $t('transpress-learn-more') }}
          </router-link>
        </div>

      </div>

      </div>
    </section>

    <section class="stats">
      <div class="container">
        <h2 class="text-style-home-heading stats-heading">{{ $t('partnership-stats-title') }}</h2>
        <p class="subtitle text-style-home-subheading stats-subtitle">{{ $t('partnership-stats-subtitle') }}</p>
        <div class="stats-grid">
          <div class="stat">
            <div class="value tight">1<span class="ot-word ot-word--wider-right">{{ $t('partnership-of') }}</span>4</div>
            <div class="label">{{ $t('partnership-stats-item-1') }}</div>
          </div>
          <div class="stat">
            <div class="value accent">29%</div>
            <div class="label">{{ $t('partnership-stats-item-2') }}</div>
          </div>
          <div class="stat">
            <div class="value pale">80%</div>
            <div class="label">{{ $t('partnership-stats-item-3') }}</div>
          </div>
          <div class="stat">
            <div class="value accent">56%</div>
            <div class="label">{{ $t('partnership-stats-item-4') }}</div>
          </div>
          <div class="stat">
            <div class="value pale">26%</div>
            <div class="label">{{ $t('partnership-stats-item-5') }}</div>
          </div>
          <div class="stat">
            <div class="value">6.2%</div>
            <div class="label">{{ $t('partnership-stats-item-6') }}</div>
          </div>
        </div>
      </div>
    </section>

    <section class="why">
      <div class="container">
        <h2 class="text-style-home-heading">
          <span class="d-none d-md-block">
            <span class="fixed-line">{{ $t('partnership-why-title-line-1') }}</span>
            <span class="fixed-line">{{ $t('partnership-why-title-line-2') }}</span>
          </span>
          <span class="d-md-none">
            <span class="fixed-line">{{ $t('partnership-why-title-mobile-line-1') }}</span>
            <span class="fixed-line">{{ $t('partnership-why-title-mobile-line-2') }}</span>
            <span class="fixed-line">{{ $t('partnership-why-title-mobile-line-3') }}</span>
          </span>
        </h2>
        <div class="why-grid">
          <div class="stat">
            <div class="value why-1 tight-why">
              <span class="tight-why__part">1</span>
              <span class="tight-why__of">{{ $t('partnership-of') }}</span>
              <span class="tight-why__part">2</span>
            </div>
            <div class="label text-style-home-subheading">{{ $t('partnership-why-item-1') }}</div>
          </div>
          <div class="stat">
            <div class="value why-2 tight-why">
              <span class="tight-why__part">1</span>
              <span class="tight-why__of">{{ $t('partnership-of') }}</span>
              <span class="tight-why__part">3</span>
            </div>
            <div class="label text-style-home-subheading">{{ $t('partnership-why-item-2') }}</div>
          </div>
          <div class="stat">
            <div class="value why-3 tight-why">
              <span class="tight-why__part">3</span>
              <span class="tight-why__of">{{ $t('partnership-of') }}</span>
              <span class="tight-why__part">4</span>
            </div>
            <div class="label text-style-home-subheading">{{ $t('partnership-why-item-3') }}</div>
          </div>
        </div>
        <p class="impact text-style-home-subtitle">
          {{ $t('partnership-impact') }}
        </p>
      </div>
    </section>

    <section class="benefits">
      <div class="container">
        <h2 class="text-style-home-heading">{{ $t('partnership-benefits-title') }}</h2>
        <div class="benefits-grid">
          <div class="benefit b1">
            <div class="circle">1</div>
            <p class="text-style-home-subheading">{{ $t('partnership-benefits-item-1') }}</p>
          </div>
          <div class="benefit b2">
            <div class="circle">2</div>
            <p class="text-style-home-subheading">{{ $t('partnership-benefits-item-2') }}</p>
          </div>
          <div class="benefit b3">
            <div class="circle">3</div>
            <p class="text-style-home-subheading">{{ $t('partnership-benefits-item-3') }}</p>
          </div>
        </div>
      </div>
    </section>

    <section class="cta">
      <h3 class="cta-title text-style-home-subheading">{{ $t('partnership-cta-title') }}</h3>
      <p class="text-style-home-subtitle">
        {{ $t('partnership-cta-body') }}
      </p>
      <button class="cta-btn" type="button" @click="openContactModal">
        {{ $t('partnership-contact-btn') }}
      </button>
    </section>

    <div class="footer-wrap">
      <FooterComponent :show-logos="false" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import NavigationBar from '@/components/NavigationBar.vue';
import FooterComponent from '@/components/Components/FooterComponent.vue';
import ModalCloseBtn from '@/components/Components/ModalComponent/ModalCloseBtn.vue';

// const isZdravetoVideoPlaying = ref(false);
// const zdravetoVideoId = 'aWsV5InK380';
// const zdravetoYoutubeUrl = 'https://www.youtube.com/watch?v=aWsV5InK380';

const isContactModalOpen = ref(false);
const isSuccessModalOpen = ref(false);
const contactFirstName = ref('');
const contactLastName = ref('');
const contactEmail = ref('');
const contactCompany = ref('');
const contactMessage = ref('');

const isContactFormValid = computed(() => {
  const email = contactEmail.value || '';
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
});

function openContactModal() {
  isContactModalOpen.value = true;
}

function closeContactModal() {
  isContactModalOpen.value = false;
  contactFirstName.value = '';
  contactLastName.value = '';
  contactEmail.value = '';
  contactCompany.value = '';
  contactMessage.value = '';
}

const formspreeContactEndpoint = 'https://formspree.io/f/maqdayjn';

async function submitContactForm() {
  if (!isContactFormValid.value) return;

  try {
    const formData = new FormData();
    formData.append('Име', contactFirstName.value);
    formData.append('Фамилия', contactLastName.value);
    formData.append('Имейл адрес', contactEmail.value);
    formData.append('Име на компания', contactCompany.value);
    formData.append('Съобщение', contactMessage.value);
    await fetch(formspreeContactEndpoint, {
      method: 'POST',
      body: formData,
      headers: { Accept: 'application/json' },
    });
  } catch (e) {
    console.warn('Formspree contact submit failed:', e);
  }

  closeContactModal();
  isSuccessModalOpen.value = true;
}

function closeSuccessModal() {
  isSuccessModalOpen.value = false;
}

watch([isContactModalOpen, isSuccessModalOpen], ([contact, success]) => {
  document.body.style.overflow = (contact || success) ? 'hidden' : '';
});
</script>

<style scoped lang="scss">
@import "~@/assets/scss/fonts.scss";
@import "~@/assets/scss/variables.scss";

/* Reusable text styles from home page (HomePrimaryHero) – use these classes where you want to match home typography */

/* From: "Достъпна психологическа грижа за всяка майка" (home hero h2) – for main headings. Uses Rubik-Light for exact thin look. */
.text-style-home-heading {
  font-family: $font-thin;
  font-size: 50px;
  font-weight: 300;
  line-height: 1;
  letter-spacing: 0;
  color: $color-purple;
}

/* From: "Премахваме стигмата около следродилната депресия и насърчаваме търсенето на помощ" (home hero h3) – for subheadings */
.text-style-home-subheading {
  font-family: $font-regular; /* match app/home default Rubik-Regular */
  font-size: 22px;
  font-weight: 500;
  line-height: normal; /* HomePrimaryHero h3 does not set line-height */
  color: $color-purple;
}

/* From: "Бъдете сред първите от общността на Momfident" (home hero .sub-title) – for subtitle / tagline text */
.text-style-home-subtitle {
  font-family: $font-regular; /* match app/home default Rubik-Regular */
  font-size: 18px;
  font-weight: 400;
  line-height: 1.4;
  letter-spacing: 0;
  color: $color-purple;
}

/* Forces exact line breaks for headings/subtitles */
.fixed-line {
  display: block;
  white-space: nowrap;
  word-break: keep-all;
}

.org-page {
  background: #fdfdfd;
  color: $color-purple;
  font-family: 'Rubik', sans-serif;
}

/* Contact modal – same look as report download modal */
.contact-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(100, 100, 100, 0.7);
  z-index: 9999999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
}

.contact-modal {
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
  background: #ffffff;
  border-radius: 20px;
  position: relative;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
}

.contact-modal__close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.contact-modal__body {
  padding: 28px 28px 26px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.contact-modal__label {
  font-family: $font-regular;
  font-size: 14px;
  font-weight: 500;
  line-height: 1;
  color: $color-purple;
  margin: 0;
}

.contact-modal__input,
.contact-modal__textarea {
  width: 100%;
  border-radius: 14px;
  border: 2px solid rgba(107, 107, 156, 0.25);
  padding: 0 14px;
  font-family: $font-regular;
  font-size: 14px;
  color: $color-purple;
  outline: none;
}

.contact-modal__input {
  height: 48px;
}

.contact-modal__textarea {
  min-height: 100px;
  padding: 14px;
  resize: vertical;
}

.contact-modal__input:focus,
.contact-modal__textarea:focus {
  border-color: rgba(252, 103, 139, 0.85);
  box-shadow: 0 0 0 4px rgba(252, 103, 139, 0.18);
}

.contact-modal__submit {
  width: 100%;
  max-width: 360px;
  margin: 6px auto 0;
  background: #FC678B;
  border: none;
  color: #fff;
  padding: 12px 38px;
  border-radius: 999px;
  height: 48px;
  font-family: 'Rubik-Regular', sans-serif;
  font-weight: 400;
  font-size: 13px;
  line-height: 1;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  box-shadow: 0 4px 12px rgba(252, 103, 139, 0.35);
}

.contact-modal__submit:hover:not(:disabled) {
  background: #EA708C;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(252, 103, 139, 0.45);
}

.contact-modal__submit--disabled {
  background: #d7d7e2 !important;
  box-shadow: none !important;
  cursor: not-allowed !important;
  transform: none !important;
}

.contact-modal__submit--disabled:disabled {
  opacity: 1;
}

.contact-modal__body--success {
  padding: 40px 32px 36px;
  gap: 10px;
}

.contact-success__title {
  font-family: $font-regular;
  font-size: 18px;
  font-weight: 600;
  color: $color-purple;
  margin: 0;
}

.contact-success__text {
  font-family: $font-regular;
  font-size: 15px;
  color: $color-purple;
  margin: 0;
  line-height: 1.5;
}

.nav-wrap {
  max-width: 1320px;
  margin: 0 auto;
  padding: 0;
  padding-left: 8px;
}

.container {
  max-width: 1320px;
}

.hero {
  margin-top: 10px;
  margin-bottom: 40px;
}

.hero-card {
  background: #ffeef4;
  border-radius: 30px;
  overflow: hidden;
  align-items: center;
  min-height: 480px;
}

.hero-left {
  padding: 60px 50px;
  position: relative;
  z-index: 2;

  h1 {
    font-family: $font-thin; /* Rubik-Light – same thin look as home hero "Достъпна психологическа грижа за всяка майка" */
    font-size: 50px;
    font-weight: 300;
    line-height: 1;
    letter-spacing: 0;
    color: $color-purple; /* #6B6B9C */
    margin-bottom: 24px;
  }

  .main-text {
    margin: 0 0 20px 0;
    max-width: 90%;
  }

  .secondary-text {
    margin-bottom: 30px;
    max-width: 90%;
    opacity: 0.8;
  }
}

.hero-right {
  display: flex;
  align-items: stretch;
  justify-content: center;
  min-height: 100%;
  align-self: stretch;
  position: relative;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    /* show the RIGHT part of ection.png (where the mother is) */
    object-position: 98% center;
    display: block;
    position: relative;
    z-index: 1;

    /* Fade only the very left edge into the pink background (shows more of the photo) */
    -webkit-mask-image: linear-gradient(
      to right,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 1) 14%,
      rgba(0, 0, 0, 1) 100%
    );
    mask-image: linear-gradient(
      to right,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 1) 14%,
      rgba(0, 0, 0, 1) 100%
    );
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    -webkit-mask-size: 100% 100%;
    mask-size: 100% 100%;
  }
}

.cta-btn {
  margin-top: 14px;
  background: #FC678B;
  border: none;
  color: #fff;
  padding: 12px 38px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  min-width: 280px;
  font-family: 'Rubik-Regular', sans-serif;
  font-weight: 400;
  font-size: 13px;
  line-height: 1;
  letter-spacing: 0.3px;
  box-shadow: 0 4px 12px rgba(252, 103, 139, 0.35);
  text-transform: uppercase;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.cta-btn:hover {
  background: #EA708C;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(252, 103, 139, 0.45);
}

.cta-btn:active {
  transform: translateY(0);
  box-shadow: 0 3px 10px rgba(252, 103, 139, 0.35);
}

.examples {
  padding: 50px 0 70px;
  background: #ffffff;
  text-align: center;
  font-family: 'Rubik', sans-serif;

  .examples-title {
    font-family: 'Rubik', sans-serif;
    font-size: 28px;
    font-weight: 500;
    font-style: normal;
    line-height: 1;
    letter-spacing: 0;
    text-align: center;
    color: $color-purple;
    margin: -6px 0 22px;
  }

  .examples-card-wrap {
  display: flex;
  justify-content: center;
  align-items: stretch;
  gap: 34px;
}

  .example-card {
    width: 100%;
    max-width: 360px;
    background: #FBE9EC;
    border-radius: 24px;
    padding: 18px 18px 16px;
  }
  

  .thumb {
    border-radius: 18px;
    overflow: hidden;
    background: #ffffff;
    padding: 10px;
    position: relative;
    box-shadow: 0 10px 22px rgba(0, 0, 0, 0.12);

    iframe {
      width: 100%;
      height: 170px;
      border: 0;
      border-radius: 14px;
      display: block;
    }

    .thumb__button {
      position: relative;
      width: 100%;
      border: none;
      background: transparent;
      padding: 0;
      margin: 0;
      cursor: pointer;
      display: block;
    }

    .thumb__play {
      position: absolute;
      inset: 0;
      display: grid;
      place-items: center;

      &::before {
        content: "";
        width: 72px;
        height: 72px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.82);
        box-shadow: 0 10px 22px rgba(0, 0, 0, 0.18);
      }

      &::after {
        content: "";
        position: absolute;
        width: 0;
        height: 0;
        border-left: 16px solid rgba(55, 55, 88, 0.9);
        border-top: 11px solid transparent;
        border-bottom: 11px solid transparent;
        transform: translateX(3px);
      }
    }

    .thumb__youtube-link {
      position: absolute;
      bottom: 18px;
      right: 18px;
      z-index: 3;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      height: 30px;
      padding: 0 12px;
      border-radius: 999px;
      text-decoration: none;
      font-family: 'Rubik', sans-serif;
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 0.2px;
      color: #ffffff;
      background: rgba(0, 0, 0, 0.55);
      backdrop-filter: blur(6px);
      -webkit-backdrop-filter: blur(6px);
      transition: transform 0.15s ease, background 0.15s ease;
    }

    .thumb__youtube-link:hover {
      background: rgba(0, 0, 0, 0.68);
      transform: translateY(-1px);
    }

    img {
      width: 100%;
      height: 170px;
      object-fit: cover;
      border-radius: 14px;
      display: block;
    }
  }

  // .example-body {
  //   margin-top: 14px;
  //   font-family: $font-regular;
  //   font-size: 13px;
  //   font-weight: 500;
  //   color: $color-purple;
  //   text-align: left;
  //   padding: 0 6px;
  // }

  .example-body {
  padding: 10px 2px 0;
  font-size: 14px;
  line-height: 1.35;
  color: $color-purple;
  height: 60px;
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
}

  .read-more {
    margin-top: 14px;
    width: 100%;
    height: 42px;
    border-radius: 999px;
    border: none;
    background: #6C6396;
    color: #ffffff;
    font-family: 'Rubik', sans-serif;
    font-size: 12px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
    box-shadow: 0 6px 16px rgba(108, 99, 150, 0.28);
  }

  .read-more:hover {
    background: #5f568f;
    transform: translateY(-2px);
    box-shadow: 0 8px 18px rgba(108, 99, 150, 0.34);
  }
}

.stats {
  background: #6c6396;
  color: white;
  padding: 70px 0 65px;
  text-align: center;
  font-family: 'Rubik', sans-serif;

  h2 {
    margin-bottom: 10px;
  }

  /* Keep the home-heading typography, but make it readable on the dark stats background */
  .stats-heading {
    color: #ffffff;
  }

  .subtitle {
    margin-bottom: 48px;
    margin-top: 40px; /* Moved down more */
    opacity: 0.95;
  }

  /* Keep the home-subheading typography, but make it readable on the dark stats background */
  .stats-subtitle {
    color: #ffffff;
  }

  .stats-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(200px, 1fr));
    gap: 32px 48px;
    margin: 0 auto;
    max-width: 1100px;
  }

  .stat {
    padding: 0 10px;
  }

  .value {
    font-family: 'Rubik', sans-serif;
    font-size: 64px;
    font-weight: 900;
    line-height: 90px;
    letter-spacing: 0;
    text-align: center;
  }

  .label {
    font-size: 16px;
    line-height: 1.2;
    margin-top: 8px;
    margin-bottom: 5px;
    max-width: 260px;
    margin-left: auto;
    margin-right: auto;
    font-weight: 700;
    letter-spacing: 0;
    text-align: center;
  }

  .value.accent {
    color: #fc678b;
  }

  .value.pale {
    color: #f7c7d2;
  }

  .value.tight {
    letter-spacing: -1px;
    word-spacing: -12px;
  }

  /* Equal spacing around "of/от" */
  .value.tight .ot-word {
    display: inline-block;
    margin: 0 2px;
  }

  /* Only for "1 от 4" – add a bit more space before the 4 */
  .value.tight .ot-word.ot-word--wider-right {
    margin-right: 10px !important;
  }

  .value.accent + .label {
    color: #fc678b;
  }

  .value.pale + .label {
    color: #f7c7d2;
  }
}

.why {
  padding: 70px 0 80px;
  text-align: center;
  font-family: 'Rubik', sans-serif;

  h2 {
    font-size: 42px; /* Updated size */
    font-weight: 500; /* Updated weight */
    margin-bottom: 40px;
    line-height: 1; /* Updated line-height */
    letter-spacing: 0;
    text-align: center;
    color: #675d92;
  }

  .why-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 40px;
    margin-bottom: 60px;
    align-items: start;
  }

  .stat .value {
    font-size: 70px;
    font-weight: 900;
    line-height: 1.05;
  }

  /* Equal space between num | от | num (replaces uneven word-spacing) */
  .tight-why {
    display: inline-flex;
    align-items: baseline;
    justify-content: center;
    gap: 0.22em;
    letter-spacing: 0;
    text-transform: lowercase;
    white-space: nowrap;
  }

  .stat .label {
    margin-top: 12px;
    max-width: 360px;
    margin-left: auto;
    margin-right: auto;
  }

  .why-1 {
    color: #f9b4c2;
  }

  .why-2 {
    color: #675d92;
  }

  .why-3 {
    color: #f6507e;
  }

  .impact {
    text-align: center;
    max-width: 1100px;
    margin: 0 auto;
    /* Keep the exact same wrapping as before applying shared styles */
    font-size: 19px;
    line-height: 1.2;
  }
}

.benefits {
  background: #FBE9EC; /* Added background */
  padding: 70px 0 70px; /* Increased bottom padding */
  text-align: center;
  font-family: 'Rubik', sans-serif;

  h2 {
    margin-bottom: 56px;
  }

  .benefits-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 40px;
    align-items: start;
  }

  .benefit {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 32px;
    color: #5a5583;
    text-align: center;
  }
  
  .benefit p {
    margin-bottom: 5px; /* Paragraph spacing */
  }
  
  .benefit.b1 {
    color: #6B6B9C;
  }

  .benefit.b2 {
    color: #FC5D7E;
  }
  
  .benefit.b3 {
    color: #EC8497;
  }

  .circle {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 800;
    font-size: 32px;
    color: #fff;
    box-shadow: 0 6px 14px rgba(0, 0, 0, 0.08);
  }

  .b1 .circle {
    background: #6B6B9C;
  }

  .b2 .circle {
    background: #fe7495;
  }

  .b3 .circle {
    background: #EC8497; /* Updated color */
  }
}

.cta {
  text-align: center;
  padding: 70px 0 60px;
  background: #ffffff; /* Changed to white */
  margin: 0 calc(-1 * ( (100vw - 100%) / 2));
  padding-left: calc((100vw - 100%)/2);
  padding-right: calc((100vw - 100%)/2);

  .cta-title {
    margin-top: 20px;
    margin-bottom: 32px;
    /* Slightly bigger than subheading, smaller than hero heading */
    font-size: 28px;
  }

  p {
    max-width: 820px;
    margin: 0 auto 36px;
  }

  .cta-btn {
    /* keep the same button sizing as the hero CTA */
    margin-top: 22px;
    margin-bottom: 32px;
  }
}

.footer-wrap :deep(.p-5) {
  display: none !important;
}

/* Page-specific header tweaks */
.nav-wrap :deep(.logo) {
  margin-left: 10px;
}

.nav-wrap :deep(.download-btn) {
  margin-right: 10px;
}

@media (max-width: 992px) {
  .hero-left {
    padding: 30px;
  }
  .hero-card {
    min-height: auto;
  }
  .hero-right {
    display: none;
  }

  .hero-left h1 {
    font-size: 30px;
  }
  .text-style-home-heading {
    font-size: 30px;
  }
  .text-style-home-subheading {
    font-size: 15px;
  }

  .cta .cta-title {
    font-size: 20px;
  }
}

@media (max-width: 768px) {
  .nav-wrap {
    max-width: 100%;
    padding-left: 12px;
    padding-right: 12px;
  }

  .container {
    max-width: 100%;
    padding-left: 16px;
    padding-right: 16px;
  }

  /* Prevent horizontal overflow/white space on the right on mobile */
  .org-page {
    overflow-x: hidden;
  }

  .hero-left {
    padding: 32px 15px;
    text-align: center;

    h1 {
      font-size: 28px;
      margin-bottom: 14px;
      text-align: center;
      word-spacing: 0.5px;
      hyphens: auto;
      -webkit-hyphens: auto;
      -ms-hyphens: auto;
    }

    .main-text {
      text-align: center;
      max-width: 95%;
      margin-left: auto;
      margin-right: auto;
      word-spacing: 0.2px;
      padding: 0 2px;
    }

    .secondary-text {
      text-align: center;
      max-width: 100%;
      margin-left: auto;
      margin-right: auto;
      word-spacing: 0.3px;
      padding: 0 5px;
    }

    .cta-btn {
      margin-left: auto;
      margin-right: auto;
    }

    p {
      font-size: 14px;
      text-align: center;
      line-height: 1.4;
    }
  }

  .cta .cta-title {
    font-size: 18px;
    margin-bottom: 24px;
  }

  .examples {
    h2 {
      font-size: 18px;
    }

    :deep(.row) {
      flex-wrap: nowrap;
      overflow-x: auto;
      gap: 12px;
      padding-bottom: 8px;
    }

    :deep(.col-12) {
      flex: 0 0 240px;
      max-width: 240px;
    }

    .thumb img {
      height: 140px;
    }

    .example-body {
      font-size: 13px;
    }

    .read-more {
      width: 100%;
    }
  }

  .stats {
    padding: 50px 16px;

    .subtitle {
      margin-bottom: 28px;
    }

    .stats-grid {
      grid-template-columns: 1fr !important;
      gap: 35px !important;
      display: flex !important;
      flex-direction: column !important;
      align-items: center !important;
    }

    .stat {
      width: 100% !important;
      max-width: 100% !important;
      display: flex !important;
      flex-direction: column !important;
      align-items: center !important;
    }

    .value {
      font-size: 44px;
      line-height: 90px;
    }

    .value.tight {
      word-spacing: -6px !important;
      letter-spacing: -0.5px;
    }
    
    .value.tight .ot-word {
      margin: 0 2px;
    }

    .label {
      font-size: 14px;
      line-height: 1.4;
      max-width: 100%;
      padding: 0 10px;
      margin-top: 10px;
    }
  }

  .why {
    padding: 50px 16px 30px;

    h2 {
      font-size: 22px;
      margin-bottom: 24px;
    }

    /* Allow the heading lines to wrap on mobile (prevents cut-off) */
    .fixed-line {
      white-space: normal;
      overflow-wrap: anywhere;
      word-break: normal;
    }

    .why-grid {
      grid-template-columns: 1fr;
      gap: 24px;
    }

    .stat .value {
      font-size: 48px;
    }

    .stat .label {
      max-width: 100%;
    }

    .impact {
      max-width: 100%;
      /* Keep the exact same wrapping as before applying shared styles */
      font-size: 16px;
      line-height: 1.4;
    }
  }

  .benefits {
    padding: 50px 16px 30px;

    h2 {
      margin-bottom: 32px;
    }

    .benefits-grid {
      grid-template-columns: 1fr;
      gap: 28px;
    }

    .benefit {
      gap: 20px;

      p {
        margin-bottom: 0;
      }
    }

    .circle {
      width: 70px;
      height: 70px;
      font-size: 28px;
    }
  }

  .cta {
    padding: 50px 16px 40px;
    margin: 0;
    padding-left: 0;
    padding-right: 0;

    .cta-title {
      margin-bottom: 24px;
    }

    p {
      max-width: 100%;
      margin-left: auto;
      margin-right: auto;
      word-spacing: normal;
      letter-spacing: normal;
      padding: 0;
      hyphens: none;
      -webkit-hyphens: none;
      -ms-hyphens: none;
      text-align: center;
      white-space: normal;
      overflow-wrap: break-word;
      word-break: normal;
    }
    
    p br {
      display: none;
    }

    .cta-btn {
      width: 100%;
      max-width: 360px;
    }
  }

  .text-style-home-heading {
    font-size: 24px;
  }

  /* Match hero heading to stats heading size on mobile (overrides general .text-style-home-heading so it applies) */
  .hero-left h1.text-style-home-heading,
  .stats .stats-heading {
    font-size: 24px;
  }

  .text-style-home-subheading {
    font-size: 14px;
  }
  .text-style-home-subtitle {
    font-size: 13px;
  }
}

/* Ensure the 3-line hero heading fits on very small screens */
@media (max-width: 480px) {
  .hero-left h1 {
    font-size: 20px;
  }
}

@media (max-width: 360px) {
  .hero-left h1 {
    font-size: 18px;
  }
}
</style>

